import React, { useEffect, useMemo, useState } from "react";
import { Drawer, Popover } from "@mantine/core";
import Icon from "./icons";
import WeekPicker from "./WeekPicker";
import schedulerService from "../../../services/scheduler";
import { DOW, MON_SHORT, fmtRange, weekLabel, startOfWeek, addDays, isoDate, entryMeta } from "./scheduleUtils";
import css from "./Scheduler.module.css";
import "./SchedulerCreate.css";

const idOf = (o) => (typeof o === "object" ? o?._id || o?.id : o);
const initials = (name) =>
  (name || "").trim().split(/\s+/).slice(0, 2).map((w) => w[0] || "").join("").toUpperCase() || "?";

// Copy a week's ASSIGNED staff shifts into a target week — appointments and
// open shifts are never duplicated. Grouped by staff, each shift individually
// selectable. Copies are created directly in the target week (published like
// any other shift; assigned staff are notified). Backed by
// POST /schedule/copy-week (from, to, shiftIds).
export default function CopyWeekDrawer({ opened, onClose, sourceWeekStart, shifts = [], staff = [], onCopied }) {
  const [target, setTarget] = useState(() => startOfWeek(addDays(sourceWeekStart || new Date(), 7)));
  const [dpMonth, setDpMonth] = useState(() => {
    const d = new Date(target);
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [pickOpen, setPickOpen] = useState(false);
  const [expanded, setExpanded] = useState(() => new Set());
  // Per-shift selection: everything is included by default; the admin can
  // untick shifts (or whole staff groups) they don't want copied.
  const [excluded, setExcluded] = useState(() => new Set());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!opened || !sourceWeekStart) return;
    const t = startOfWeek(addDays(sourceWeekStart, 7)); // default: the next week
    setTarget(t);
    setDpMonth(new Date(t.getFullYear(), t.getMonth(), 1));
    setExpanded(new Set());
    setExcluded(new Set());
    setPickOpen(false);
  }, [opened, sourceWeekStart]);

  // The week's ASSIGNED staff shifts, grouped by staff — appointments and
  // open shifts are excluded: the duplication feature is for assigned shifts
  // only. Each shift is individually selectable.
  const groups = useMemo(() => {
    const byStaff = new Map();
    (shifts || []).forEach((s) => {
      if (s.kind === "appointment") return;
      const sid = idOf(s.staff);
      if (!sid) return;
      if (!byStaff.has(sid)) byStaff.set(sid, []);
      byStaff.get(sid).push(s);
    });
    const sortShifts = (arr) =>
      arr.slice().sort((a, b) => new Date(a.date) - new Date(b.date) || a.startMin - b.startMin);
    return (staff || [])
      .map((st) => ({ ...st, shifts: sortShifts(byStaff.get(st.value) || []) }))
      .filter((g) => g.shifts.length > 0);
  }, [shifts, staff]);

  const totalShifts = useMemo(() => groups.reduce((a, g) => a + g.shifts.length, 0), [groups]);
  const selectedIds = useMemo(
    () => groups.flatMap((g) => g.shifts.map((s) => idOf(s))).filter((id) => !excluded.has(String(id))),
    [groups, excluded]
  );

  const toggleShift = (id) =>
    setExcluded((prev) => {
      const n = new Set(prev);
      const k = String(id);
      if (n.has(k)) n.delete(k);
      else n.add(k);
      return n;
    });
  // Group checkbox: any included -> untick all; none included -> tick all.
  const toggleGroup = (g) =>
    setExcluded((prev) => {
      const n = new Set(prev);
      const ids = g.shifts.map((s) => String(idOf(s)));
      const anyIncluded = ids.some((id) => !n.has(id));
      ids.forEach((id) => (anyIncluded ? n.add(id) : n.delete(id)));
      return n;
    });

  const toggle = (id) =>
    setExpanded((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  const copy = async () => {
    if (!sourceWeekStart || !target || !selectedIds.length) return;
    setBusy(true);
    await schedulerService.copyWeek(isoDate(sourceWeekStart), isoDate(target), selectedIds, () => {
      onCopied?.(target);
      onClose?.();
    });
    setBusy(false);
  };

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size={520}
      padding={0}
      withCloseButton={false}
      title={null}
      classNames={{ content: "sdDrawerContent" }}
      styles={{ body: { height: "100%", padding: 0 } }}
    >
      <div className={css.dwWrap}>
        <div className={css.dwHead}>
          <div>
            <h3 className={css.dwTitle}>Copy schedule</h3>
            <div className={css.dwSub}>From week of {sourceWeekStart ? weekLabel(sourceWeekStart) : ""}</div>
          </div>
          <button type="button" className={css.dwClose} onClick={onClose} aria-label="Close"><Icon name="x" size={16} /></button>
        </div>

        <div className={css.dwBody}>
          <section className={css.dwSection}>
            <h5 className={css.dwSectionH}>Copy to week</h5>
            <div style={{ display: "flex", justifyContent: "center" }}>
              <div className={css.dateNav}>
                <button type="button" className={css.navArrow} onClick={() => setTarget(startOfWeek(addDays(target, -7)))} aria-label="Previous week"><Icon name="chevronLeft" size={15} /></button>
                <Popover opened={pickOpen} onChange={setPickOpen} position="bottom" withinPortal shadow="md" radius={16}>
                  <Popover.Target>
                    <button type="button" className={css.dateLabel2} style={{ minWidth: 150 }} onClick={() => setPickOpen((o) => !o)} aria-haspopup="dialog">{weekLabel(target)}</button>
                  </Popover.Target>
                  <Popover.Dropdown p={0}>
                    <WeekPicker
                      mode="week"
                      monthDate={dpMonth}
                      selWeek={startOfWeek(target)}
                      onMonthChange={setDpMonth}
                      onPick={(d) => { setTarget(startOfWeek(d)); setDpMonth(new Date(d.getFullYear(), d.getMonth(), 1)); setPickOpen(false); }}
                      onClose={() => setPickOpen(false)}
                    />
                  </Popover.Dropdown>
                </Popover>
                <button type="button" className={css.navArrow} onClick={() => setTarget(startOfWeek(addDays(target, 7)))} aria-label="Next week"><Icon name="chevron" size={15} /></button>
              </div>
            </div>
          </section>

          <section className={css.dwSection}>
            <h5 className={css.dwSectionH}>Staff shifts in this week</h5>
            <div style={{ fontSize: 12, color: "var(--app-text-muted)", margin: "-4px 0 10px" }}>
              Untick any shifts you don't want copied. Assigned staff are notified of their new shifts.
            </div>
            {groups.length === 0 ? (
              <div style={{ fontSize: 13, color: "var(--app-text-muted)", padding: "8px 0" }}>No staff shifts scheduled in this week.</div>
            ) : (
              <div className={css.tblCard} style={{ overflow: "hidden" }}>
                {groups.map((g, i) => {
                  const open = expanded.has(g.value);
                  return (
                    <div key={g.value} style={{ borderTop: i > 0 ? "1px solid var(--app-border)" : "none" }}>
                      <div className={css.click} onClick={() => toggle(g.value)} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", cursor: "pointer" }}>
                        <input
                          type="checkbox"
                          checked={g.shifts.some((sh) => !excluded.has(String(idOf(sh))))}
                          onChange={() => toggleGroup(g)}
                          onClick={(e) => e.stopPropagation()}
                          style={{ accentColor: "var(--app-primary)", width: 15, height: 15, flex: "none", cursor: "pointer" }}
                          aria-label={`Include ${g.label}`}
                        />
                        <span className={css.tsAv}>{g.photo ? <img src={g.photo} alt="" /> : initials(g.label)}</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div className={css.numStrong} style={{ fontSize: 14 }}>{g.label}</div>
                          <div style={{ fontSize: 12, color: "var(--app-text-muted)" }}>
                            {g.shifts.filter((sh) => !excluded.has(String(idOf(sh)))).length} of {g.shifts.length} selected
                          </div>
                        </div>
                        <span style={{ color: "#cbd5e1", display: "inline-flex", transform: open ? "rotate(90deg)" : "none", transition: "transform .15s" }}><Icon name="chevron" size={16} /></span>
                      </div>
                      {open ? (
                        <div style={{ padding: "0 14px 12px 50px" }}>
                          {g.shifts.map((s) => {
                            const d = new Date(s.date);
                            const meta = entryMeta(s);
                            const where = meta.cat === "facility" ? (meta.facility || "Facility shift") : (meta.clientName || "Client visit");
                            const included = !excluded.has(String(idOf(s)));
                            return (
                              <div key={idOf(s)} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0", fontSize: 12.5, color: "var(--app-text)", borderTop: "1px dashed var(--app-border)", opacity: included ? 1 : 0.45 }}>
                                <input
                                  type="checkbox"
                                  checked={included}
                                  onChange={() => toggleShift(idOf(s))}
                                  style={{ accentColor: "var(--app-primary)", width: 14, height: 14, flex: "none", cursor: "pointer" }}
                                  aria-label="Include shift"
                                />
                                <span style={{ width: 60, color: "var(--app-text-muted)", flex: "none" }}>{DOW[d.getDay()]} {MON_SHORT[d.getMonth()]} {d.getDate()}</span>
                                <span className={css.numStrong} style={{ width: 116, flex: "none" }}>{s.allDay ? "All day" : fmtRange(s.startMin, s.endMin)}</span>
                                <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{where}</span>
                              </div>
                            );
                          })}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        </div>

        <div className={css.dwFoot}>
          <button type="button" className={css.btnG} onClick={onClose}>Cancel</button>
          <span style={{ flex: 1 }} />
          <button type="button" className={css.btnP} onClick={copy} disabled={busy || selectedIds.length === 0}>
            <Icon name="copy" size={16} /> Copy {selectedIds.length} shift{selectedIds.length === 1 ? "" : "s"} to {weekLabel(target)}
          </button>
        </div>
      </div>
    </Drawer>
  );
}
